import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  IVisitRangeOptions,
  useGetCustomersList,
} from "src/api/customers/list";
import { ClearIcon } from "@mui/x-date-pickers";
import SearchIcon from "@mui/icons-material/Search";
import { db } from "src/firebase";
import { query, collection, onSnapshot } from "firebase/firestore";
import { ICarBrand } from "src/types/generic";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";

const tableHeadText = [
  "Customer Name",
  "Email",
  "Mobile",
  "Gender",
  "No. of Vehicles",
  "No. of Visits",
  "Plate Type",
  "Car Brand",
];

const CustomersTable = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [tempPlateType, setTempPlateType] = useState<string>("all");
  const [plateType, setPlateType] = useState<string>("all");
  const [tempCustomerType, setTempCustomerType] = useState<string>("all");
  const [customerType, setCustomerType] = useState<string>("all");
  const [searchBy, setSearchBy] = useState<string>("");
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [tempCarBrand, setTempCarBrand] = useState<ICarBrand | null>(null);
  const [carBrand, setCarBrand] = useState<ICarBrand | null>(null);
  const [tempGender, setTempGender] = useState<string>("all");
  const [gender, setGender] = useState<string>("all");
  const [carBrands, setCarBrands] = useState<ICarBrand[]>([]);
  const [tempVehiclesNum, setTempVehiclesNum] = useState<string>("all");
  const [vehiclesNum, setVehiclesNum] = useState<string>("all");
  const [vehicleNumberOptions, setVehicleNumberOptions] = useState<number[]>(
    [],
  );
  const [allVisitsGroups, setAllVisitsGroups] = useState<IVisitRangeOptions[]>(
    [],
  );
  const [tempVisitsGroups, setTempVisitsGroups] = useState<
    IVisitRangeOptions[]
  >([]);
  const [visitsGroups, setVisitsGroups] = useState<IVisitRangeOptions[]>([]);

  const visitGroupsToSend = visitsGroups.map(({ min, max }) => `${min}-${max}`);

  const filters = {
    ...(searchBy && { search: searchBy }),
    ...(vehiclesNum != "all" && { vehiclesCount: +vehiclesNum }),
    ...(visitsGroups.length > 0 && { visitRanges: visitGroupsToSend }),
    ...(plateType !== "all" && { plateType }),
    ...(customerType !== "all" && { isInternal: customerType === "internal" }),
    ...(gender !== "all" && { gender }),
    ...(carBrand && { brandId: carBrand.id }),
  };

  // APIs
  const { data, isLoading } = useGetCustomersList({
    ...filters,
    page: page + 1,
    limit: rowsPerPage,
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeGender = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempGender(value);
  };

  const handleChangePlateType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempPlateType(value);
  };

  const handleChangeCustomerType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempCustomerType(value);
  };

  const handleChangeCarBrand = (event: React.SyntheticEvent, newValue: any) => {
    setTempCarBrand(newValue);
  };

  const handleSearch = (searchedVal: string) => {
    setTempSearchBy(searchedVal);
  };

  const handleChangeVehicleNum = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempVehiclesNum(value);
  };

  const clearSearch = () => {
    setTempSearchBy("");
  };

  const handleApply = () => {
    setSearchBy(tempSearchBy);
    setCarBrand(tempCarBrand);
    setGender(tempGender);
    setCustomerType(tempCustomerType);
    setPlateType(tempPlateType);
    setVehiclesNum(tempVehiclesNum);
    setVisitsGroups(tempVisitsGroups);
  };

  const handleResetFilers = () => {
    setSearchBy("");
    setCarBrand(null);
    setGender("all");
    setPlateType("all");
    setCustomerType("all");
    setVehiclesNum("all");
    setVisitsGroups([]);

    setTempSearchBy("");
    setTempCarBrand(null);
    setTempGender("all");
    setTempPlateType("all");
    setTempCustomerType("all");
    setTempVehiclesNum("all");
    setTempVisitsGroups([]);
  };

  const hasData = data && data?.data?.length > 0;

  //return to 1st page after filters change
  useEffect(() => {
    setPage(0);
  }, [gender, carBrand, customerType, plateType, searchBy]);

  useEffect(() => {
    if (data) {
      setTotalCount(data?.meta.total);
      setVehicleNumberOptions(data?.vehicleCountOptions);
      setAllVisitsGroups(data?.visitRangeOptions);
    }
  }, [data]);

  // fetch car brands
  useEffect(() => {
    let unsubscribe: any;
    if (unsubscribe) {
      unsubscribe();
    }
    unsubscribe = onSnapshot(
      query(collection(db, "car_brands")),
      (QuerySnapshot) => {
        const fetchedCarBrands: any[] = [];
        QuerySnapshot.forEach((doc) => {
          fetchedCarBrands.push({ ...doc.data(), id: doc.id });
        });

        setCarBrands(fetchedCarBrands);
      },
    );

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  console.log({ carBrands });

  return (
    <>
      {/* filters */}
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={3}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          {/* search */}
          <TextField
            size="small"
            placeholder="Search"
            sx={{ width: { xs: "100%", sm: "220px" } }}
            value={tempSearchBy}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* customer type */}
          <FormControl
            fullWidth
            size="small"
            sx={{ width: { xs: "100%", sm: "135px" } }}
          >
            <InputLabel id="demo-simple-select-label">Customer Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempCustomerType}
              label="Customer Type"
              onChange={handleChangeCustomerType}
              fullWidth
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"customer"}>Customer</MenuItem>
              <MenuItem value={"internal"}>Internal</MenuItem>
            </Select>
          </FormControl>
          {/* plate type */}
          <FormControl
            fullWidth
            size="small"
            sx={{ width: { xs: "100%", sm: "135px" } }}
          >
            <InputLabel id="demo-simple-select-label">Plate Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempPlateType}
              label="Plate Type"
              onChange={handleChangePlateType}
              fullWidth
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"saudi"}>Saudi</MenuItem>
              <MenuItem value={"non-saudi"}>Non-Saudi</MenuItem>
            </Select>
          </FormControl>

          {/* car  brand */}
          <Box sx={{ width: { xs: "100%", sm: "135px" } }}>
            <CustomAutocomplete
              size="small"
              fullWidth
              disablePortal
              id="combo-box-demo"
              options={carBrands}
              getOptionLabel={(option: any) => option.nameEn}
              getOptionKey={(option: any) => option.id}
              value={tempCarBrand}
              onChange={handleChangeCarBrand}
              renderInput={(params: any) => (
                <TextField fullWidth {...params} label={"Car Brand"} />
              )}
            />
          </Box>

          {/* gender */}
          <FormControl
            fullWidth
            size="small"
            sx={{ width: { xs: "100%", sm: "105px" } }}
          >
            <InputLabel id="demo-simple-select-label">Gender</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempGender}
              label="Gender"
              onChange={handleChangeGender}
              fullWidth
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"male"}>Male</MenuItem>
              <MenuItem value={"female"}>Female</MenuItem>
            </Select>
          </FormControl>
          {/* no of vehicles */}
          <FormControl
            fullWidth
            size="small"
            sx={{ width: { xs: "100%", sm: "115px" } }}
          >
            <InputLabel id="demo-simple-select-label">
              No. of Vehicles
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempVehiclesNum}
              label="No. of Vehicles"
              onChange={handleChangeVehicleNum}
              fullWidth
            >
              <MenuItem value={"all"}>All</MenuItem>
              {vehicleNumberOptions?.map((count) => {
                return (
                  <MenuItem key={count} value={count}>
                    {count}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            size="small"
            sx={{ width: { xs: "100%", sm: "135px" } }}
          >
            <InputLabel>No. of Visits</InputLabel>
            <Select
              multiple
              value={tempVisitsGroups.map((item) => item.label)}
              label="No. of Visits"
              onChange={(event) => {
                const labels = event.target.value as string[];

                setTempVisitsGroups(
                  allVisitsGroups.filter((item) => labels.includes(item.label)),
                );
              }}
              renderValue={(selected) => (selected as string[]).join(", ")}
            >
              {allVisitsGroups.map((item) => (
                <MenuItem key={item.label} value={item.label}>
                  <Checkbox
                    checked={tempVisitsGroups.some(
                      (g) => g.label === item.label,
                    )}
                    size="small"
                    sx={{ padding: 0, paddingX: 1 }}
                  />
                  <ListItemText primary={item.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction={"row"}>
          <LoadingButton onClick={handleResetFilers}>Clear</LoadingButton>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </LoadingButton>
        </Stack>
      </Stack>
      <Paper
        sx={{
          bgcolor: "#1A1A1A",
          boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
          padding: { xs: "15px", md: "30px" },
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                {tableHeadText.map((item) => (
                  <TableCell
                    key={item}
                    sx={{
                      fontWeight: 600,
                      color: "#A1A5B7",
                    }}
                  >
                    {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableLoadingSkeleton rowsLength={rowsPerPage} cellsLength={8} />
            ) : (
              <>
                {hasData ? (
                  <TableBody>
                    {data?.data.map((row, index: number) => (
                      <TableRow
                        key={row.firebaseUid}
                        sx={{
                          "&:last-child td, &:last-child th": {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          {row.phoneNumber1 ?? ""} <br />{" "}
                          {row.phoneNumber2 ?? ""}
                        </TableCell>
                        <TableCell>{row.gender}</TableCell>
                        <TableCell>{row.vehiclesCount}</TableCell>
                        <TableCell>{row.visitsCount}</TableCell>
                        <TableCell>{row.plateTypes?.join(", ")}</TableCell>
                        <TableCell>{row.carBrands?.join(", ")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ) : null}
              </>
            )}
          </Table>

          {!hasData && !isLoading && <NoData />}

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
          />
        </TableContainer>
      </Paper>
    </>
  );
};
export default CustomersTable;
